import React from 'react';
import { Box, Button, Container, Grid2 as Grid, Typography } from '@mui/material';

import { cloudinaryImageUrl } from '../../Services/Cloudinary';
import BaseImage from '../DesignSystem/BaseImage/BaseImage';
import VideoEmbed from '../DesignSystem/VideoEmbed';
import { HtmlToMUIMapper } from '../../Utils';
import { UiSection as UiSectionTypes } from '../../Utils/Types/UiSection';

export interface UiSectionProps {
  section?: UiSectionTypes;
  backgroundColor: string;
  titleTypographyComponent?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  children?: React.ReactNode;
}

function UiSection({
  section,
  backgroundColor,
  titleTypographyComponent = 'h2',
  children,
  ...rest
}: UiSectionProps) {
  const {
    title = null,
    content = null,
    cta_label: ctaLabel = null,
    cta_url: ctaUrl = null,
    picture = null,
    video_embed: videoUrl = null,
    alignment,
  } = section || {};
  const pictureUrl = cloudinaryImageUrl(picture?.s3_key, 'h_460,q_auto:low,c_fill', picture?.title);
  const hasPictureOrVideo = !!pictureUrl || !!videoUrl;
  const isCentered = alignment === 'center';

  const renderPictureOrVideo = () => {
    if (videoUrl) {
      return <VideoEmbed title="video player" src={videoUrl} rounded />;
    }
    return <BaseImage src={pictureUrl} ratio="fullHeight" alt="layer picture" rounded />;
  };

  const ctaButton = ctaLabel && ctaUrl && (
    <Button variant="outlined" color="primary" href={ctaUrl}>
      {ctaLabel}
    </Button>
  );

  // Build layout based on alignment
  const gridDirection = alignment === 'right' ? 'row-reverse' : 'row';
  const contentWidth = hasPictureOrVideo && !isCentered ? 7 : 12;
  const justifyContent = isCentered ? 'center' : 'flex-start';

  return (
    <Box sx={{ backgroundColor, wordBreak: 'break-word' }} py={8} {...rest}>
      <Container maxWidth="md">
        <Grid container spacing={4} sx={{ alignItems: justifyContent }} direction={gridDirection}>
          <Grid size={{ md: contentWidth, xs: 12 }}>
            {title && (
              <Typography
                component={titleTypographyComponent}
                variant="h4"
                gutterBottom
                align={isCentered ? 'center' : 'left'}
              >
                {title}
              </Typography>
            )}

            {/* For centered layout, render media between title and content */}
            {hasPictureOrVideo && isCentered && (
              <Grid size={{ md: 12, xs: 12 }}>{renderPictureOrVideo()}</Grid>
            )}

            <HtmlToMUIMapper content={content} />

            {ctaButton && (
              <Box
                sx={{
                  display: 'flex',
                  mt: 4,
                  justifyContent,
                }}
              >
                {ctaButton}
              </Box>
            )}
          </Grid>

          {/* For left/right layout, render media in its own column */}
          {hasPictureOrVideo && !isCentered && (
            <Grid size={{ md: 'grow', xs: 12 }}>{renderPictureOrVideo()}</Grid>
          )}
        </Grid>

        {children}
      </Container>
    </Box>
  );
}

export default UiSection;
